import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppIconComponent } from '../../common/components/icon/icon.component';
import getSafeIconName from '../../common/functions/get-safe-icon-name';
import {
  ReactiveFormsModule,
  FormsModule,
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  ValidatorFn,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
import {
  buildBeneficiaryGroup,
  mapBeneficiariesToUpsert,
} from './components/checkout-beneficiaries/checkout-beneficiaries.component';
import {
  beneficiaryPercentageSumValidator,
  minBeneficiariesValidator,
} from '../../common/utils/beneficiary-validators';
import { SearchableDropdownComponent } from '../../common/components/searchable-dropdown/searchable-dropdown.component';

export function documentExpiryValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const expiryCtrl = group.get('documentExpiresAt');
    const birthCtrl = group.get('birthDate');

    if (!expiryCtrl || !birthCtrl || !expiryCtrl.value || !birthCtrl.value) {
      return null;
    }

    const parseDate = (val: string) => {
      if (typeof val !== 'string') return null;
      const isValid = (date: Date, year: number, month: number, day: number) =>
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day;

      const ddmmyyyy = val.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (ddmmyyyy) {
        const [, d, m, y] = ddmmyyyy;
        const day = Number(d);
        const month = Number(m);
        const year = Number(y);
        const date = new Date(year, month - 1, day);
        return Number.isNaN(date.getTime()) || !isValid(date, year, month, day)
          ? null
          : date;
      }

      const yyyymmdd = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (yyyymmdd) {
        const [, y, m, d] = yyyymmdd;
        const day = Number(d);
        const month = Number(m);
        const year = Number(y);
        const date = new Date(year, month - 1, day);
        return Number.isNaN(date.getTime()) || !isValid(date, year, month, day)
          ? null
          : date;
      }

      return null;
    };

    const expiryDate = parseDate(expiryCtrl.value);
    const birthDate = parseDate(birthCtrl.value);

    if (expiryDate && birthDate) {
      if (expiryDate <= birthDate) {
        if (!expiryCtrl.hasError('expiresBeforeBirth')) {
          expiryCtrl.setErrors({ ...expiryCtrl.errors, expiresBeforeBirth: true });
        }
        return { expiresBeforeBirth: true };
      } else {
        if (expiryCtrl.hasError('expiresBeforeBirth')) {
          const errors = { ...expiryCtrl.errors };
          delete errors['expiresBeforeBirth'];
          expiryCtrl.setErrors(Object.keys(errors).length > 0 ? errors : null);
        }
      }
    }
    return null;
  };
}

import { forkJoin, of, lastValueFrom, timeout, catchError, Subscription } from 'rxjs';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { QuotesService } from '../../core/data/services/quotes.service';
import { QuoteResponseDto } from '../../core/data/dtos/quote-response.dto';
import { QuoteAnswerDto } from '../../core/data/dtos/quote-answer.dto';
import { scrollToFirstInvalidControl } from '../../common/utils/form-utils';
import { bmiValidator } from '../../common/utils/bmi-validators';
import { QuoteDetailDto } from '../../core/data/dtos/quote-detail.dto';
import { QuoteDetailUpsertDto } from '../../core/data/dtos/quote-detail-upsert.dto';
import { QuoteSummaryDto } from '../../core/data/dtos/quote-summary.dto';
import { QuoteInsuredPersonUpsertDto } from '../../core/data/dtos/quote-insured-person-upsert.dto';
import { QuoteCoverageRequestDto } from '../../core/data/dtos/quote-coverage-request.dto';
import { UpdateQuoteRequestDto } from '../../core/data/dtos/update-quote-request.dto';
import { Gender } from '../../core/data/enums/gender.enum';
import { getCategoryLabel } from '../../common/utils/insurance-category.util';
import {
  InsuranceCategory,
  InsuranceTypeDto,
} from '../../core/data/dtos/insurance-type.dto';
import { InsuranceQuestionGroupDto } from '../../core/data/dtos/insurance-question-group.dto';
import {
  InsuranceQuestionDto,
  QuestionType,
} from '../../core/data/dtos/insurance-question.dto';
import { UploadsService } from '../../core/data/services/uploads.service';
import { FileUploadState } from './components/checkout-questions/checkout-questions.component';
import { LocalStoreService } from '../../common/services/storage.service';
import { QuoteAnswerUpsertDto } from '../../core/data/dtos/quote-answer-upsert.dto';
import { environment } from '../../../environments/environment';
import { ColonialLocationsService } from '../../core/data/services/colonial-locations.service';
import { SwalService } from '../../common/services/swal.service';
import { CountryDto } from '../../core/data/dtos/country.dto';
import { StateDto } from '../../core/data/dtos/state.dto';
import { TownDto } from '../../core/data/dtos/town.dto';
import { ZipFullDescDto } from '../../core/data/dtos/zip-full-desc.dto';
import { CheckoutDetailComponent } from './components/checkout-detail/checkout-detail.component';
import { CheckoutContactComponent } from './components/checkout-contact/checkout-contact.component';
import { CheckoutInsuredFormComponent } from './components/checkout-insured-form/checkout-insured-form.component';
import { CheckoutQuestionsComponent } from './components/checkout-questions/checkout-questions.component';
import { CheckoutUploadDocumentsComponent } from './components/checkout-upload-documents/checkout-upload-documents.component';
import { CheckoutPaymentComponent } from './components/checkout-payment/checkout-payment.component';
import { OccupationsService } from '../../features/insurance-types/services/occupations.service';
import { OccupationDto } from '../../core/data/dtos/occupation.dto';
import { ConfigurationService } from '../../core/data/services/configuration.service';
import { Personeria } from '../../core/data/enums/personeria.enum';
import { ExchangeRateService } from '../../core/data/services/exchange-rate.service';
import { toIsoDate, toDisplayDate } from '../../common/utils/date-utils';
import {
  DEFAULT_PASSPORT_EXPIRATION_YEARS,
  PASSPORT_MASK,
  isoDatePlusYears,
  passportExpiryValidator,
  passportNumberValidator,
} from '../../common/utils/document-validators';
import { cmToFeetInches, normalizeProducerCode, denormalizeProducerCode } from '../../common/utils/quote-detail-mappers';

type ModalType =
  | 'info-person'
  | 'questions'
  | 'upload-documents'
  | 'payment'
  | 'requested-by';
type QuestionKey = 'chronicIllness' | 'recentSurgery' | 'currentMedication';
type EconomicActivity =
  | 'empleado'
  | 'propietario'
  | 'jubilado'
  | 'inversionista'
  | 'independiente'
  | 'estudiante'
  | 'ama_casa'
  | 'otro';
type IncomeRange =
  | '0-50000'
  | '50000-100000'
  | '100000-150000'
  | '150000+'
  | null;
type PaymentAmountOption = 'total' | 'minimum' | 'other';

@Component({
  selector: 'app-checkout',
  imports: [
    CommonModule,
    AppIconComponent,
    ReactiveFormsModule,
    FormsModule,
    NgxMaskDirective,
    SearchableDropdownComponent,

    // Child components
    CheckoutDetailComponent,
    CheckoutContactComponent,
    CheckoutContactComponent,
    CheckoutInsuredFormComponent,
    CheckoutQuestionsComponent,
    CheckoutUploadDocumentsComponent,
    CheckoutPaymentComponent,
  ],
  templateUrl: './checkout.component.html',
  styleUrl: './checkout.component.css',
})
export class CheckoutComponent implements OnInit, OnDestroy {
  // Summary state for aligning premium values with Summary screen
  summary: QuoteSummaryDto | null = null;
  isSummaryLoading = true;

  activeModal: ModalType | null = null;
  modalTitle = '';
  modalIcon = '';
  modalDescription = '';
  questionStates: Record<QuestionKey, boolean> = {
    chronicIllness: false,
    recentSurgery: false,
    currentMedication: false,
  };
  economicActivity: EconomicActivity | null = null;
  incomeRange: IncomeRange = null;
  otherIncomeEnabled = false;
  otherIncomeAmount = '';
  otherIncomeDescription = '';
  paymentAmountOption: PaymentAmountOption = 'total';
  paymentOtherAmount = '';
  termsAccepted = true;
  readonly getCategoryLabel = getCategoryLabel;
  readonly QuestionType = QuestionType;
  currentQuote: QuoteResponseDto | null = null;
  // Location data and selections for Contacto y Dirección
  countries: CountryDto[] = [];
  defaultCountryCode: number | null = null;
  states: StateDto[] = [];
  towns: TownDto[] = [];
  filteredStatesByDetail: Map<number, StateDto[]> = new Map();
  zipFullDescsByDetail: Map<number, ZipFullDescDto[]> = new Map();
  requestedByZipFullDescs: ZipFullDescDto[] = [];
  // Track which detail is the source for "useAsPrimaryForAll"
  useAsPrimaryForAllDetailId: number | null = null;
  // Track if the contratante (requester) is not the primary insured
  isContratanteNotInsured: boolean = false;
  // Track payment loading state
  isPaymentLoading = false;
  // Occupations for RequestedBy form dropdown
  occupations: OccupationDto[] = [];
  activeQuestionGroup: InsuranceQuestionGroupDto | null = null;
  minimumPaymentPercentage = 0;
  minimumPaymentAmount = 0;
  private initialLoadComplete = false;
  private pendingCardnetParams: any = null;
  protected readonly getSafeIconName = getSafeIconName;
  protected readonly Number = Number;
  protected readonly InsuranceCategory = InsuranceCategory;
  protected readonly String = String;
  private readonly quotes = inject(QuotesService);
  private readonly fb = inject(FormBuilder);
  // Details form group: keys are detail IDs
  detailsForm: FormGroup = this.fb.group({});
  // Reactive form for RequestedBy data used in 'requested-by' modal
  requestedByFg: FormGroup = this.fb.group({
    personeria: [Personeria.Fisica],
    name: [null, [Validators.required, Validators.maxLength(40)]],
    lastName: [null, [Validators.required, Validators.maxLength(40)]],
    relationship: [null, [Validators.required]],
    street: [null, [Validators.required, Validators.maxLength(150)]],
    countryCode: [{ value: null, disabled: true }],
    stateCode: [null, [Validators.required]],
    zipFullDescCode: [null, [Validators.required]],
    documentType: ['IdCard'], // values: 'IdCard' | 'Passport'
    documentNumber: [null, [Validators.required]],
    tipoComprobante: [null, [Validators.required]],
  }, { validators: passportNumberValidator() });
  Personeria = Personeria; // expose enum to template
  questionsFg: FormGroup = this.fb.group({});
  // Reactive form for person data used in 'info-person' modal
  personFg: FormGroup = this.fb.group({
    personeria: [Personeria.Fisica],
    name: [null, [Validators.required, Validators.maxLength(40)]],
    lastName: [null, [Validators.required, Validators.maxLength(40)]],
    documentType: [null, [Validators.required]], // values: 'Passport' | 'IdCard'
    documentNumber: [null, [Validators.required]],
    // Validators depend on the document type (applyDocumentTypeRules): disabled for a cédula,
    // required and capped at today + passportExpirationYears for a passport.
    documentExpiresAt: [{ value: null, disabled: true }],
    birthDate: [null, [Validators.required]],
    gender: [null, [Validators.required]], // values: 'M' | 'F'
    civilState: [null, [Validators.required]], // values: 'Married' | 'Single'
    heightFeet: [null, [Validators.required, Validators.pattern(/^\d+$/), Validators.min(1)]],
    heightInches: [null, [Validators.required, Validators.pattern(/^\d+$/), Validators.min(0), Validators.max(11)]],
    heightCm: [null, [Validators.required, Validators.min(1)]],
    // weightLb is the canonical value (always pounds) used for validation and
    // submission everywhere else; weightDisplay/weightUnit are the UI-facing
    // controls kept in sync with it by CheckoutInsuredFormComponent.
    weightLb: [null, [Validators.required, Validators.min(50)]],
    weightDisplay: [null, [Validators.required, Validators.min(1)]],
    weightUnit: ['lb'],
    nacionality: [null, [Validators.required]], // values: 'Dominican' | 'Foreign'
    personAddressId: [null],
    isPep: [null, [Validators.required]],
    occupation: [null],
    primaryInsuredRelation: [null], // Only for secondary?
  }, {
    validators: [
      documentExpiryValidator(),
      passportNumberValidator(),
      // Same BMI rule the quote step applies to the primary insured (option modal).
      bmiValidator({ enabled: () => this.isPrincipalInsuredModal }),
    ],
  });

  /** Whether the person modal is editing the primary insured. */
  get isPrincipalInsuredModal(): boolean {
    return this.modalTitle.toLowerCase().includes('asegurado principal');
  }
  /** Backoffice parameter: passport expiration date = today + X years (prefill and maximum). */
  passportExpirationYears = DEFAULT_PASSPORT_EXPIRATION_YEARS;

  /** Latest passport expiration date allowed (ISO), today + passportExpirationYears. */
  get passportExpiryMax(): string {
    return isoDatePlusYears(this.passportExpirationYears);
  }
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly uploads = inject(UploadsService);
  private readonly locations = inject(ColonialLocationsService);
  private readonly store = inject(LocalStoreService);
  private readonly swal = inject(SwalService);
  private readonly occupationsService = inject(OccupationsService);
  private readonly configService = inject(ConfigurationService);
  private readonly exchangeRateService = inject(ExchangeRateService);
  // temp map for file selections per question id
  fileSelections = new Map<number, File>();
  /** Per-question upload status; files upload when selected so "Continuar" only saves the answers. */
  uploadStates = new Map<number, FileUploadState>();
  private readonly uploadSubs = new Map<number, Subscription>();

  get hasUploadsInProgress(): boolean {
    return Array.from(this.uploadStates.values()).some((u) => u.status === 'uploading');
  }
  uploadedDocuments: Record<string, File> = {};
  private principalInsuredIds: {
    insuredId: number | null;
    personId: number | null;
  } | null = null;
  private selectedPersonCtx: {
    detailId: number;
    insuredId: number;
    personId: number | null;
  } | null = null;

  constructor(private el: ElementRef) {
    this.personFg.get('documentType')?.valueChanges.subscribe(() => this.applyDocumentTypeRules());
    // get current quote from service
    this.currentQuote = this.quotes.currentQuote;
    this.initPerDetailStateFromQuote(this.currentQuote);
    // Load summary initially if possible
    this.loadSummary();
    this.quotes.currentQuote$.subscribe((q) => {
      this.currentQuote = q;

      // Auto-check contratante checkbox when requestedBy fields are filled (user previously saved contractor data)
      const hasRequestedByData = !!(q?.requestedByName || q?.requestedByDocumentNumber);
      if (hasRequestedByData) {
        this.isContratanteNotInsured = true;
      } else if (((q?.details?.length ?? 0) > 1 && !this.useAsPrimaryForAllDetailId)) {
        // Also auto-check if we have multiple details and they are not sharing the same primary insured
        this.isContratanteNotInsured = true;
      } else if (!this.initialLoadComplete) {
        // Enforce off by default on initial load for applicable cases
        this.isContratanteNotInsured = false;
      }
      // Always load the form data if available, so it's ready if the section is shown
      this.loadRequestedByFromQuote();

      this.initPerDetailStateFromQuote(q);
      // Preload person form for principal insured when quote changes,
      // but only if the info-person modal is NOT currently open (the same
      // personFg is shared across primary/spouse/child forms and resetting
      // it mid-edit would wipe the user's input).
      if (this.activeModal !== 'info-person') {
        try {
          this.loadPrincipalInsuredIntoForm();
        } catch { }
      }
      // Refresh summary when quote changes
      this.loadSummary();
    });
  }

  get paymentTotalAmount(): number {
    return this.summary?.totalGrossPremium ?? 0;
  }

  get isPaymentAmountInvalid(): boolean {
    if (this.paymentAmountOption !== 'other') return false;
    const valStr = String(this.paymentOtherAmount || '');
    const cleanAmount = valStr.replace(/,/g, '');
    const parsed = Number(cleanAmount);
    // Invalid if parsed is greater than total, OR if it's parsed as 0 (optional constraint, but requested constraint is 'exceeds total')
    // The user strictly asked: "if otra cantidad exceeds the total to pay"
    return !isNaN(parsed) && (parsed > this.paymentTotalAmount || parsed < this.minimumPaymentAmount);
  }

  get quoteCurrency(): string {
    const details = this.currentQuote?.details ?? [];
    if (details.length > 0 && details[0]?.currency) {
      const cur = details[0].currency.toString().trim().toUpperCase();
      return cur === 'USD' ? 'US$' : 'RD$';
    }
    return 'RD$';
  }

  get isRequestedByComplete(): boolean {
    return !!this.currentQuote?.requestedByName;
  }

  get documentMask(): string {
    const personeria = this.personFg.get('personeria')?.value;
    if (personeria === Personeria.Moral) return '0-00-00000-0';
    const type = this.personFg.get('documentType')?.value;
    if (type === 'IdCard') return '000-0000000-0';
    if (type === 'Passport') return PASSPORT_MASK;
    return '';
  }

  get documentLabel(): string {
    return this.personFg.get('personeria')?.value === Personeria.Moral ? 'RNC' : 'No. de documento';
  }

  get contractorDocMask(): string {
    const personeria = this.requestedByFg.get('personeria')?.value;
    if (personeria === Personeria.Moral) return '0-00-00000-0';
    const docType = this.requestedByFg.get('documentType')?.value;
    if (docType === 'Passport') return PASSPORT_MASK;
    return '000-0000000-0';
  }

  get contractorDocLabel(): string {
    const personeria = this.requestedByFg.get('personeria')?.value;
    if (personeria === Personeria.Moral) return 'RNC';
    return 'No. de documento';
  }

  get contractorNameLabel(): string {
    return this.requestedByFg.get('personeria')?.value === Personeria.Moral ? 'Razón social' : 'Nombres';
  }

  get contractorIsMoral(): boolean {
    return this.requestedByFg.get('personeria')?.value === Personeria.Moral;
  }

  onContractorPersoneriaChange(): void {
    const personeria = this.requestedByFg.get('personeria')?.value;
    if (personeria === Personeria.Fisica) {
      this.requestedByFg.get('documentNumber')?.setValue(null);
      this.requestedByFg.get('tipoComprobante')?.setValue(32);
      this.requestedByFg.get('documentType')?.setValue('IdCard');
      this.requestedByFg.get('documentType')?.enable();
      this.requestedByFg.get('lastName')?.setValidators([Validators.required, Validators.maxLength(40)]);
    } else {
      // Moral -> force RNC, lock document type, lastName not required
      this.requestedByFg.get('documentNumber')?.setValue(null);
      this.requestedByFg.get('documentType')?.setValue('RNC');
      this.requestedByFg.get('documentType')?.disable();
      this.requestedByFg.get('lastName')?.clearValidators();
      this.requestedByFg.get('lastName')?.setValue(null);
    }
    this.requestedByFg.get('lastName')?.updateValueAndValidity();
  }



  /** Returns the tipoComprobante value to use as ncfType during issuance */
  get resolvedNcfType(): number {
    return this.requestedByFg.get('tipoComprobante')?.value ?? 32;
  }

  // Per-detail filtered getters; values are cached to keep stable references for ng-select
  getFilteredStates = (detailId: number): StateDto[] => {
    return this.filteredStatesByDetail.get(detailId) ?? [];
  };

  getFilteredZipFullDescs = (detailId: number): ZipFullDescDto[] => {
    return this.zipFullDescsByDetail.get(detailId) ?? [];
  };

  // Returns the per-detail net premium from the latest loaded summary
  detailNetPremium(detailId: number): number | null {
    const d = this.summary?.details?.find((x) => x.detailId === detailId);
    return d?.netPremium ?? null;
  }

  ngOnInit(): void {
    // Preload location lists and reference data without blocking the page UI
    this.swal.dismiss();
    forkJoin({
      countries: this.locations.getCountries().pipe(
        timeout(15000),
        catchError(() => of([] as CountryDto[])),
      ),
      states: this.locations.getStates().pipe(
        timeout(15000),
        catchError(() => of([] as StateDto[])),
      ),
      towns: this.locations.getTowns().pipe(
        timeout(15000),
        catchError(() => of([] as TownDto[])),
      ),
      occupations: this.occupationsService.getOccupations().pipe(
        timeout(15000),
        catchError(() => of([] as OccupationDto[])),
      ),
      minPayment: this.configService.getMinimumPaymentPercentage().pipe(
        timeout(15000),
        catchError(() => of(0)),
      ),
      passportYears: this.configService.getPassportExpirationYears().pipe(
        timeout(15000),
        catchError(() => of(DEFAULT_PASSPORT_EXPIRATION_YEARS)),
      ),
    }).subscribe({
      next: ({ countries, states, towns, occupations, minPayment, passportYears }) => {
        this.passportExpirationYears = passportYears > 0 ? passportYears : DEFAULT_PASSPORT_EXPIRATION_YEARS;
        this.applyDocumentTypeRules();
        this.countries = countries || [];

        // Find Dominican Republic to set as default and disable selection
        const dr = this.countries.find(c => c.countryName?.toUpperCase().includes('DOMINICAN'));
        if (dr && dr.countryCode != null) {
          this.defaultCountryCode = dr.countryCode;
          // Apply to already created detail forms and disable
          Object.keys(this.detailsForm.controls).forEach(key => {
            const fg = this.detailsForm.get(key) as FormGroup;
            const countryCtrl = fg?.get('countryCode');
            if (countryCtrl) {
              const currentVal = countryCtrl.value;
              if (currentVal !== this.defaultCountryCode) {
                countryCtrl.setValue(this.defaultCountryCode, { emitEvent: false });
                fg.get('stateCode')?.setValue(null, { emitEvent: false });
                fg.get('zipFullDescCode')?.setValue(null, { emitEvent: false });
              }
              countryCtrl.disable();
            }
          });
          // Also set default country on requestedByFg
          const rbCountry = this.requestedByFg.get('countryCode');
          if (rbCountry) {
            rbCountry.setValue(this.defaultCountryCode, { emitEvent: false });
            rbCountry.disable();
          }
        }

        this.states = states || [];
        this.towns = towns || [];
        this.occupations = occupations || [];
        this.minimumPaymentPercentage = minPayment || 0;
        this.refreshAllFilteredStates();
        this.calculateMinimumPayment();
      },
      error: () => {
        // Even on error, stop showing the loader so the page is usable
        console.warn('Some initial data failed to load');
        this.finishInitialLoad();
      },
      complete: () => {
        this.finishInitialLoad();
      },
    });

    // Handle Cardnet redirect back with session validation
    this.route.queryParamMap.subscribe(async (params) => {
      if (this.initialLoadComplete) {
        this.handleCardnetRedirect(params);
      } else {
        this.pendingCardnetParams = params;
      }
    });
  }

  private finishInitialLoad(): void {
    this.initialLoadComplete = true;

    if (this.pendingCardnetParams) {
      const params = this.pendingCardnetParams;
      this.pendingCardnetParams = null;
      this.handleCardnetRedirect(params);
    }
  }

  private async handleCardnetRedirect(params: any): Promise<void> {
    const session = params.get('session');
    const sessionKey = params.get('sessionKey');
    const success = params.get('success');
    const errorParam = params.get('error') || params.get('reason');

    if (!session || !sessionKey) return;

    const storedSession = this.store.getSessionItem<string>('cardnetSession');
    const storedSessionKey =
      this.store.getSessionItem<string>('cardnetSessionKey');

    if (storedSession === session && storedSessionKey === sessionKey) {
      if (success === 'true') {
        // Read additional params provided on success
        const authorizationCode =
          params.get('AuthorizationCode') ||
          params.get('authorizationCode') ||
          '';
        const transactionId =
          params.get('TransactionId') || params.get('transactionId') || '';

        // Show loading state instead of success alert as requested
        this.swal.showLoading('Pago exitoso. Emitiendo póliza...');

        // Clear query params to avoid retriggering alerts on reload
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true,
        });

        // Proceed to issue the quote
        const q = this.currentQuote;
        if (!q?.quoteId) return;

        const issuePayload: any = { applyPayment: true };
        const rate = this.exchangeRateService.getUsdRate();
        if (this.quoteCurrency === 'US$' && rate) {
          issuePayload.exchangeRate = rate;
        }
        issuePayload.ncfType = this.resolvedNcfType;

        try {
          await lastValueFrom(
            this.quotes.issueQuote(q.quoteId, issuePayload),
          );
          this.swal.dismiss();

          // Store summary in currentQuote so success page can display it
          if (this.summary && this.quotes.currentQuote) {
            this.quotes.setCurrentQuoteLocal({
              ...this.quotes.currentQuote,
              summary: this.summary,
            });
          }

          await this.router.navigate(['/quote/success']);
        } catch (err: any) {
          console.error('Failed to issue quote after payment', err);
          const message = typeof err === 'string'
            ? err
            : err?.error?.error || err?.error?.message || 'Error al emitir la cotización.';

          if (typeof err !== 'string') {
            this.swal.error(message);
          }
        }
      } else {
        const reason = errorParam || 'El pago no fue aprobado';
        console.error('CardNet payment failed:', reason);

        const isTesting = environment.serverHost.includes('localhost') || environment.serverHost.includes('dev');
        if (isTesting) {
          this.swal.confirm('Vemos que estás en un entorno de pruebas y el pago no fue exitoso. ¿Deseas emitir la cotización de todas formas?').then((result) => {
            if (result.isConfirmed) {
              if (this.currentQuote?.quoteId) {
                this.issueQuoteRegardlessPayment(this.currentQuote.quoteId);
              } else {
                this.swal.error('No se pudo encontrar la cotización para emitirla.');
              }
            } else {
              this.swal.error(reason);
              this.router.navigate([], {
                relativeTo: this.route,
                queryParams: {},
                replaceUrl: true,
              });
            }
          });
        } else {
          this.swal.error(reason);

          // Clear query params to avoid retriggering alerts on reload
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
            replaceUrl: true,
          });
        }
      }
    } else {
      console.warn(
        'Session or session key mismatch; ignoring redirect params.',
      );
    }
  }

  ngOnDestroy(): void {
    // Sync form values to in-memory currentQuote before leaving the page
    // This ensures contact/address data isn't lost when navigating away
    this.syncContactDataToQuote();
  }

  getDetailForm(detailId: number): FormGroup {
    return this.detailsForm.get(String(detailId)) as FormGroup;
  }

  onUseAsPrimaryForAllChange(detailId: number, enabled: boolean): void {
    if (enabled) {
      this.useAsPrimaryForAllDetailId = detailId;
      this.propagateFrom(detailId); // Syncs Contact/Address
      this.propagatePersonData(detailId); // Syncs Person Data

      // If we just checked "use for all", we revert to hiding the contratante form 
      // (per the requirement that "El contratante no es el asegurado principal" should be off by default when applicable)
      this.isContratanteNotInsured = false;
    } else if (this.useAsPrimaryForAllDetailId === detailId) {
      this.useAsPrimaryForAllDetailId = null;
      // If we uncheck "use for all" and have multiple products, we force contratante form again
      if ((this.currentQuote?.details?.length ?? 0) > 1) {
        this.isContratanteNotInsured = true;
      }
    }
  }

  onCountryChange(detailId: number, codeVal: number | string | null): void {
    // Value is already updated in form by child so we don't strictly need to set it,
    // BUT child emits the value, so we can use it for side effects.
    const form = this.getDetailForm(detailId);
    if (!form) return;

    // Reset dependent fields
    form.get('stateCode')?.setValue(null);
    form.get('zipFullDescCode')?.setValue(null);
    this.zipFullDescsByDetail.delete(detailId);
    this.refreshFilteredStatesForDetail(detailId);

    // Propagation handled by valueChanges subscription
  }

  onStateChange(detailId: number, codeVal: number | string | null): void {
    const form = this.getDetailForm(detailId);
    if (!form) return;

    // Reset dependent fields
    form.get('zipFullDescCode')?.setValue(null);

    const code = codeVal != null ? Number(codeVal) : null;
    if (code != null && !isNaN(code)) {
      this.locations.getZipFullDescs(code).subscribe({
        next: (zips) => {
          this.zipFullDescsByDetail.set(detailId, zips || []);
        },
      });
    } else {
      this.zipFullDescsByDetail.delete(detailId);
    }

    // Propagation handled by valueChanges subscription
  }

  onRequestedByStateChange(stateCode: number | null): void {
    this.requestedByFg.get('zipFullDescCode')?.setValue(null);
    this.requestedByZipFullDescs = [];
    if (stateCode != null) {
      this.locations.getZipFullDescs(stateCode).subscribe({
        next: (zips) => {
          this.requestedByZipFullDescs = zips || [];
        },
      });
    }
  }

  openModal(
    type: ModalType,
    title: string,
    icon: string,
    description = '',
    detailId?: number,
  ): void {
    this.activeModal = type;
    this.modalTitle = title;
    this.modalIcon = icon;
    this.modalDescription = description;
    // Store context if provided
    if (detailId) {
      this.selectedPersonCtx = {
        detailId,
        insuredId: 0, // Placeholder, will be resolved in loadPrincipalInsuredIntoForm
        personId: null,
      };
    }

    if (type === 'payment') {
      // Ensure latest summary is available for gross premium amount
      this.loadSummary();
      this.paymentAmountOption = 'total';
      this.paymentOtherAmount = '';
      this.termsAccepted = false;
    }

    // Load principal insured data when opening principal info modal
    if (
      type === 'info-person' &&
      title.toLowerCase().includes('asegurado principal')
    ) {
      // Pass detailId explicitly if we obtained it from the card
      this.loadPrincipalInsuredIntoForm(detailId);
    }

    if (type === 'questions' && this.activeQuestionGroup) {
      this.buildQuestionForm(this.activeQuestionGroup);
    }
  }

  openQuestionsForGroup(event: {
    group: InsuranceQuestionGroupDto;
    detailId: number;
  }): void {
    this.activeQuestionGroup = event.group;
    this.openModal(
      'questions',
      event.group.title || 'Preguntas',
      'file-user',
      event.group.description || '',
      event.detailId,
    );
  }

  onDocumentFilesChanged(files: Record<string, File>): void {
    this.uploadedDocuments = { ...files };
  }

  /** Toggle the contratante is not primary insured flag */
  onContratanteNotInsuredChange(checked: boolean): void {
    this.isContratanteNotInsured = checked;
  }

  qKey(id: number): string {
    return `q_${id}`;
  }

  getVisibleQuestions(
    group: InsuranceQuestionGroupDto,
  ): InsuranceQuestionDto[] {
    const all = group.questions || [];
    const values = this.questionsFg.value || {};
    return all.filter((q) => {
      if (!q?.dependsOnQuestionId) return true;
      const depKey = this.qKey(q.dependsOnQuestionId);
      const depVal = values[depKey];
      if (q.dependsOnValue == null) return true;
      // convert boolean controls to 'true'/'false' strings for comparison
      const depValStr =
        typeof depVal === 'boolean' ? String(depVal) : String(depVal ?? '');
      return depValStr === String(q.dependsOnValue);
    });
  }

  onFileSelected(event: { questionId: number; file: File }): void {
    const { questionId, file } = event;
    if (!file) return;
    this.fileSelections.set(questionId, file);
    const key = this.qKey(questionId);
    if (this.questionsFg.get(key)) {
      // set something non-empty to pass required validator until upload
      this.questionsFg.get(key)?.setValue(file.name);
      this.questionsFg.get(key)?.markAsDirty();
    }
    this.startUpload(questionId);
  }

  onFileRemoved(questionId: number): void {
    this.cancelUpload(questionId);
    this.fileSelections.delete(questionId);
  }

  /** Uploads the selected file of a question right away, tracking progress and errors inline. */
  startUpload(questionId: number): void {
    const file = this.fileSelections.get(questionId);
    if (!file) return;
    this.cancelUpload(questionId);
    this.uploadStates.set(questionId, { status: 'uploading', progress: 0 });

    const sub = this.uploads.uploadWithProgress(file).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          const total = event.total ?? file.size;
          const progress = total ? Math.min(99, Math.round((event.loaded / total) * 100)) : 0;
          this.uploadStates.set(questionId, { status: 'uploading', progress });
        } else if (event.type === HttpEventType.Response) {
          const body = event.body;
          const uploadId = body?.id != null ? String(body.id) : body?.url || '';
          this.uploadStates.set(
            questionId,
            uploadId
              ? { status: 'done', progress: 100, uploadId }
              : { status: 'error', progress: 0, error: 'No se pudo subir el archivo. Inténtalo de nuevo.' },
          );
        }
      },
      error: (err: HttpErrorResponse) => {
        this.uploadSubs.delete(questionId);
        this.uploadStates.set(questionId, { status: 'error', progress: 0, error: this.describeUploadError(err) });
      },
      complete: () => this.uploadSubs.delete(questionId),
    });
    this.uploadSubs.set(questionId, sub);
  }

  private cancelUpload(questionId: number): void {
    this.uploadSubs.get(questionId)?.unsubscribe();
    this.uploadSubs.delete(questionId);
    this.uploadStates.delete(questionId);
  }

  private resetUploads(): void {
    this.uploadSubs.forEach((sub) => sub.unsubscribe());
    this.uploadSubs.clear();
    this.uploadStates.clear();
  }

  private describeUploadError(err: HttpErrorResponse): string {
    if (err?.status === 413) {
      return 'El archivo es demasiado grande. El límite es de 40MB.';
    }
    if (!err || err.status === 0 || err.status === 504 || err.status === 502) {
      return 'No se pudo subir el archivo. Verifica tu conexión e inténtalo de nuevo.';
    }
    const serverMessage = typeof err.error === 'string' ? err.error : err.error?.error || err.error?.message;
    return serverMessage || 'No se pudo subir el archivo. Inténtalo de nuevo.';
  }

  closeModal(): void {
    this.activeModal = null;
    this.modalTitle = '';
    this.modalIcon = '';
    this.modalDescription = '';
    this.selectedPersonCtx = null;
  }

  onQuestionToggle(key: QuestionKey, checked: boolean): void {
    this.questionStates[key] = checked;
  }

  setEconomicActivity(activity: EconomicActivity): void {
    this.economicActivity = activity;
  }

  setIncomeRange(range: IncomeRange): void {
    this.incomeRange = range;
  }

  toggleOtherIncome(enabled: boolean): void {
    this.otherIncomeEnabled = enabled;
    if (!enabled) {
      this.otherIncomeAmount = '';
      this.otherIncomeDescription = '';
    }
  }

  setPaymentAmountOption(option: PaymentAmountOption): void {
    this.paymentAmountOption = option;
    if (option !== 'other') {
      this.paymentOtherAmount = '';
    }
  }

  openOtherInsured(
    detailId: number,
    insuredId: number,
    title: string,
    description = '',
  ): void {
    this.loadInsuredIntoForm(detailId, insuredId);
    this.openModal('info-person', title, 'circle-user-round', description);
  }

  onContinue(): void {
    if (this.activeModal === 'info-person') {
      if (this.modalTitle.toLowerCase().includes('asegurado principal')) {
        this.savePrincipalInsured();
      } else if (this.selectedPersonCtx) {
        this.saveOtherInsured();
      }
      return;
    }
    if (this.activeModal === 'questions') {
      this.saveQuestions();
      return;
    }
  }

  onBack(): void {
    this.router.navigate(['/quote/summary'], {
      queryParamsHandling: 'preserve',
    });
  }

  onPayAndIssue(): void {
    // legacy path (issuing after returning from payment)
    if (!this.termsAccepted) {
      return;
    }

    console.log('pay');
    const q = this.currentQuote;
    if (!q?.quoteId) {
      console.error('No quote available to pay');
      return;
    }

    // Determine amount based on selection. For now, use displayed total or user-entered amount.
    let amount = this.paymentTotalAmount;

    if (this.paymentAmountOption === 'minimum') {
      amount = this.minimumPaymentAmount;
    } else if (this.paymentAmountOption === 'other') {
      // paymentOtherAmount might be a number or string depending on mask config
      const valStr = String(this.paymentOtherAmount || '');
      // Remove commas if present (though mask might strip them)
      const cleanAmount = valStr.replace(/,/g, '');
      const parsed = Number(cleanAmount);

      if (isNaN(parsed) || parsed <= 0) {
        this.swal.error('Por favor, ingrese un monto válido.');
        return;
      }

      if (parsed > this.paymentTotalAmount) {
        this.swal.error('El monto a pagar no puede ser mayor al total.');
        return;
      }

      if (parsed < this.minimumPaymentAmount) {
        this.swal.error('El monto a pagar no puede ser menor al pago mínimo.');
        return;
      }

      amount = parsed;
    }

    this.isPaymentLoading = true;
    this.quotes.createPaymentSession(q.quoteId, amount).subscribe({
      next: (resp: any) => {
        const session = resp?.session || resp?.sessionKey;
        if (!session) {
          this.isPaymentLoading = false;
          console.error(
            'Payment session created but no session token provided',
            resp,
          );
          return;
        }
        // Persist session and session key for validation on return redirect
        try {
          const sVal = resp?.session != null ? String(resp.session) : '';
          const skVal = resp?.sessionKey != null ? String(resp.sessionKey) : '';
          if (sVal) this.store.setSessionItem('cardnetSession', sVal);
          if (skVal) this.store.setSessionItem('cardnetSessionKey', skVal);
        } catch { }

        // Build and submit the hidden form as required by provider
        const form = document.createElement('form');
        form.action = environment.cardnet.baseURl + 'authorize';
        form.method = 'post';
        form.style.display = 'none';

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'SESSION';
        input.value = String(session);
        form.appendChild(input);

        document.body.appendChild(form);
        this.isPaymentLoading = false;
        form.submit();
      },
      error: (err) => {
        this.isPaymentLoading = false;
        console.error('Failed to create payment session', err);
        const message = typeof err === 'string'
          ? err
          : err?.error?.error || err?.error?.message || 'El servicio de pagos no se encuentra disponible en este momento. Por favor intente más tarde.';

        const isTesting = environment.serverHost.includes('localhost') || environment.serverHost.includes('dev');
        if (isTesting) {
          this.swal.confirm('Vemos que estás en un entorno de pruebas y la pasarela de pago ha fallado. ¿Deseas emitir la cotización de todas formas?').then((result) => {
            if (result.isConfirmed && q.quoteId) {
              this.issueQuoteRegardlessPayment(q.quoteId);
            } else {
              this.swal.error(message);
            }
          });
        } else {
          this.swal.error(message);
        }
      },
    });
  }

  private async issueQuoteRegardlessPayment(quoteId: number): Promise<void> {
    this.swal.showLoading('Emitiendo póliza...');
    const issuePayload: any = { applyPayment: true };
    const rate = this.exchangeRateService.getUsdRate();
    if (this.quoteCurrency === 'US$' && rate) {
      issuePayload.exchangeRate = rate;
    }
    issuePayload.ncfType = this.resolvedNcfType;

    try {
      await lastValueFrom(
        this.quotes.issueQuote(quoteId, issuePayload),
      );
      this.swal.dismiss();

      if (this.summary && this.quotes.currentQuote) {
        this.quotes.setCurrentQuoteLocal({
          ...this.quotes.currentQuote,
          summary: this.summary,
        });
      }

      await this.router.navigate(['/quote/success']);
    } catch (err: any) {
      console.error('Failed to issue quote directly', err);
      const message = typeof err === 'string'
        ? err
        : err?.error?.error || err?.error?.message || 'Error al emitir la cotización.';
      if (typeof err !== 'string') {
        this.swal.error(message);
      }
    }
  }

  onPayNow(): void {
    const q = this.currentQuote;
    if (!q) return;

    if (this.isContratanteNotInsured) {
      // Don't reload from quote, use current form values!
      if (this.requestedByFg.invalid) {
        this.requestedByFg.markAllAsTouched();
        scrollToFirstInvalidControl(this.el);
        this.swal.error(
          'Por favor, complete los Datos del Contratante para continuar.',
        );
        return;
      }
    } else {
      // If contratante is the insured, we must still validate the 'tipoComprobante' which comes from requestedByFg
      const tc = this.requestedByFg.get('tipoComprobante');
      if (tc && tc.invalid) {
        tc.markAsTouched();
        scrollToFirstInvalidControl(this.el);
        this.swal.error('Por favor, seleccione el Tipo de comprobante.');
        return;
      }
    }

    const beneficiariesError = this.validateBeneficiaries();
    if (beneficiariesError) {
      scrollToFirstInvalidControl(this.el);
      this.swal.error(beneficiariesError);
      return;
    }

    if (this.detailsForm.invalid) {
      this.detailsForm.markAllAsTouched();
      scrollToFirstInvalidControl(this.el);
      return;
    }

    // Validate Primary Insured Data Completeness (returns failing detailId or null)
    const failingDetailId = this.validatePrimaryInsuredData(q);
    if (failingDetailId != null) {
      this.swal.error(
        'Faltan datos del asegurado principal. Por favor complete la información.',
      );
      // Open the modal for the specific detail that failed validation
      const d = q.details?.find((x) => x.id === failingDetailId);
      const ip = d?.insuredPeople?.find((i) => i.isPrimary);
      if (d && ip) {
        this.loadInsuredIntoForm(d.id!, ip.id!);
        this.openModal(
          'info-person',
          'Datos del asegurado principal',
          'circle-user-round',
          'Complete los datos requeridos',
          d.id!,
        );
      }
      return;
    }

    // Validate Question Groups
    const missingQuestionGroup = this.findFirstIncompleteQuestionGroup(q);
    if (missingQuestionGroup) {
      this.swal.error(
        'Por favor, responda todas las preguntas requeridas para continuar.',
      );
      this.openQuestionsForGroup(missingQuestionGroup);
      return;
    }

    // Save RequestedBy data if needed, then proceed
    let extraPayload = this.getApplicantPayload();

    // Validate active policy before proceeding to payment
    const primaryInsured = q.details
      ?.flatMap((d) => d.insuredPeople || [])
      ?.find((ip) => ip.isPrimary);
    const primaryDocumentNumber = primaryInsured?.person?.documentNumber ?? '';
    this.swal.showLoading('Validando...');
    this.quotes.validateActivePolicy(q.quoteId!, primaryDocumentNumber).subscribe({
      next: (result) => {
        if (result?.hasActivePolicy) {
          this.swal.error(result.message || 'El asegurado ya posee una póliza activa de este producto.');
          return;
        }
        this.updateDetailsAndPay(q, extraPayload);
      },
      error: (err) => {
        console.error('Error validating active policy', err);
        // On validation error, proceed to avoid blocking the user
        this.updateDetailsAndPay(q, extraPayload);
      },
    });
  }

  private findFirstIncompleteQuestionGroup(q: QuoteResponseDto): {
    group: InsuranceQuestionGroupDto;
    detailId: number;
  } | null {
    if (!q.details) return null;
    for (const d of q.details) {
      const groups = d.insuranceType?.questionGroups || [];
      const checkoutGroups = groups.filter((g) => g.phase === 'Checkout');
      for (const g of checkoutGroups) {
        if (!this.isGroupAnswersComplete(g, d.answers || [])) {
          return { group: g, detailId: d.id! };
        }
      }
    }
    return null;
  }

  private isGroupAnswersComplete(
    group: InsuranceQuestionGroupDto,
    answers: QuoteAnswerDto[],
  ): boolean {
    const questions = group.questions || [];
    const answerMap = new Map<number, string | null>();
    answers.forEach((a) => {
      if (a.questionId) answerMap.set(a.questionId, a.value ?? null);
    });

    const isVisible = (ques: InsuranceQuestionDto): boolean => {
      if (!ques.dependsOnQuestionId) return true;

      const depVal = answerMap.get(ques.dependsOnQuestionId);
      if (ques.dependsOnValue == null) return true;

      // convert boolean/null to string for comparison (logic from getVisibleQuestions)
      const depValStr =
        depVal != null &&
          (depVal.toLowerCase() === 'true' || depVal.toLowerCase() === 'false')
          ? depVal.toLowerCase()
          : String(depVal ?? '');

      return depValStr === String(ques.dependsOnValue).toLowerCase();
    };

    return questions.every((ques) => {
      if (!ques.required) return true;
      if (!isVisible(ques)) return true;
      const val = answerMap.get(ques.id!);
      return val != null && val !== '';
    });
  }

  private loadSummary(): void {
    const id = this.currentQuote?.quoteId ?? null;
    if (!id) {
      // fallback to any summary attached to currentQuote
      this.summary = this.currentQuote?.summary ?? null;
      this.isSummaryLoading = false;
      return;
    }
    this.isSummaryLoading = true;
    this.quotes.getQuoteSummary(id, 1).subscribe({
      next: (s) => {
        this.summary = s ?? null;
        this.isSummaryLoading = false;
        this.calculateMinimumPayment();
      },
      error: () => {
        // keep prior or fallback to quote summary
        this.summary = this.summary ?? this.currentQuote?.summary ?? null;
        this.isSummaryLoading = false;
        this.calculateMinimumPayment();
      },
    });
  }

  private calculateMinimumPayment(): void {
    if (this.summary && this.minimumPaymentPercentage > 0) {
      this.minimumPaymentAmount = (this.summary.totalGrossPremium ?? 0) * this.minimumPaymentPercentage;
    } else {
      this.minimumPaymentAmount = 0;
    }
  }

  /**
   * Syncs the contact and address form values to the in-memory currentQuote.
   * This preserves data when navigating away without explicitly saving.
   */
  private syncContactDataToQuote(): void {
    const q = this.currentQuote;
    if (!q?.details) return;

    const updatedDetails = q.details.map((d) => {
      const form = this.getDetailForm(d.id!);
      if (!form) return d;

      const fv = form.getRawValue();
      const updatedInsuredPeople = (d.insuredPeople || []).map((ip) => {
        if (!ip.isPrimary) return ip;

        // Update primary insured person with form values
        const updatedPerson = ip.person
          ? {
            ...ip.person,
            address: {
              street: fv.street ?? null,
              countryCode: fv.countryCode ?? null,
              stateCode: fv.stateCode ?? null,
              zipCode: fv.zipFullDescCode ?? null,
            },
          }
          : undefined;

        return {
          ...ip,
          contactEmail: fv.contactEmail ?? ip.contactEmail ?? null,
          homePhone: fv.homePhone ?? ip.homePhone ?? null,
          mobilePhone: fv.mobilePhone ?? ip.mobilePhone ?? null,
          person: updatedPerson,
        };
      });

      const beneficiariesArray = this.getBeneficiariesArray(d.id!);
      const beneficiaries: QuoteDetailDto['beneficiaries'] = beneficiariesArray
        ? mapBeneficiariesToUpsert(beneficiariesArray).map((b) => ({ ...b, id: b.id ?? undefined, sortOrder: b.sortOrder ?? undefined }))
        : d.beneficiaries;

      return { ...d, insuredPeople: updatedInsuredPeople, beneficiaries };
    });

    this.quotes.setCurrentQuoteLocal({ ...q, details: updatedDetails });
  }

  // Initialize maps from current quote
  private initPerDetailStateFromQuote(
    q: QuoteResponseDto | null | undefined,
  ): void {
    if (!q?.details) return;

    // Reset forms if structure changed or just patch?
    // For simplicity, we can recreate or patch. Recreating ensures correct keys.
    // existing keys
    const existingKeys = Object.keys(this.detailsForm.controls);
    const incomingKeys = q.details.map((d) => String(d.id));

    // Remove obsolete
    existingKeys
      .filter((k) => !incomingKeys.includes(k))
      .forEach((k) => {
        this.detailsForm.removeControl(k);
        this.filteredStatesByDetail.delete(Number(k));
      });

    q.details.forEach((d) => {
      const id = d.id!;
      const key = String(id);

      const primary = (d.insuredPeople || []).find((ip) => ip.isPrimary);
      const p: any = primary?.person || {};
      const addr: any = p?.address || p; // fallback to flat fields if any

      const initial = {
        contactEmail: primary?.contactEmail ?? null,
        homePhone: primary?.homePhone ?? null,
        mobilePhone: primary?.mobilePhone ?? null,
        street: addr?.street ?? null,
        countryCode: this.defaultCountryCode ?? addr?.countryCode ?? null,
        stateCode: addr?.stateCode ?? null,
        zipFullDescCode: addr?.zipCode ?? null,
      };

      if (this.detailsForm.contains(key)) {
        this.detailsForm.get(key)?.patchValue(initial, { emitEvent: false });
      } else {
        const g = this.fb.group({
          contactEmail: [
            initial.contactEmail,
            [Validators.required, Validators.email],
          ],
          homePhone: [initial.homePhone, [Validators.required]],
          mobilePhone: [initial.mobilePhone, [Validators.required]],
          street: [initial.street, [Validators.required, Validators.maxLength(150)]],
          countryCode: [
            { value: initial.countryCode, disabled: true },
            [Validators.required],
          ],
          stateCode: [initial.stateCode, [Validators.required]],
          zipFullDescCode: [initial.zipFullDescCode, [Validators.required]],
        });

        g.valueChanges.subscribe((val) => {
          if (this.useAsPrimaryForAllDetailId === id) {
            this.propagateFrom(id, val);
          }
        });

        this.detailsForm.addControl(key, g);
      }

      this.ensureBeneficiariesArray(d);

      // Preload zip full descs when a stateCode is already set (e.g. loading saved quote)
      if (initial.stateCode != null && !this.zipFullDescsByDetail.has(id)) {
        this.locations.getZipFullDescs(initial.stateCode).subscribe({
          next: (zips) => { this.zipFullDescsByDetail.set(id, zips || []); },
        });
      }

      this.refreshFilteredStatesForDetail(id);
    });
  }

  private refreshAllFilteredStates(): void {
    const q = this.currentQuote;
    if (!q?.details?.length) return;
    q.details.forEach((d) => this.refreshFilteredStatesForDetail(d.id!));
  }

  private refreshFilteredStatesForDetail(detailId: number): void {
    const form = this.getDetailForm(detailId);
    if (!form) {
      this.filteredStatesByDetail.set(detailId, []);
      return;
    }

    const country = form.get('countryCode')?.value ?? null;
    const filtered =
      country != null
        ? (this.states || []).filter((s) => s.countryCode === country)
        : [];
    this.filteredStatesByDetail.set(detailId, filtered);
  }

  private propagateFrom(sourceId: number, val?: any): void {
    const sourceGroup = this.getDetailForm(sourceId);
    if (!sourceGroup) return;

    // Use provided val or current value. Beneficiaries belong to each policy and are never copied.
    const { beneficiaries: _ignored, ...data } = val ?? sourceGroup.getRawValue();

    // We only propagate common contact/address fields
    // Ensure we don't accidentally wipe fields if val is partial?
    // Reactive forms valueChanges gives complete value of group usually.

    const q = this.currentQuote;
    if (!q?.details) return;

    q.details.forEach((d) => {
      const id = d.id!;
      if (id === sourceId) return; // skip source

      const targetGroup = this.getDetailForm(id);
      if (targetGroup) {
        targetGroup.patchValue(data, { emitEvent: false });
      }
    });
  }

  private ensureSync(detailId: number): number | null {
    const source = this.useAsPrimaryForAllDetailId;
    if (source != null && source !== detailId) {
      // Normalize changes to the source then propagate
      return source;
    }
    return null;
  }

  // New method to propagate Person Data (not just contact/address)
  private propagatePersonData(sourceId: number): void {
    const q = this.currentQuote;
    if (!q?.details) return;

    const sourceDetail = q.details.find((d) => d.id === sourceId);
    if (!sourceDetail) return;
    const sourcePrimary = sourceDetail.insuredPeople?.find(
      (ip) => ip.isPrimary,
    );
    if (!sourcePrimary?.person) return;

    const sourcePerson = sourcePrimary.person;

    const updatedDetails = q.details.map((d) => {
      if (d.id === sourceId) return d;

      const updatedInsured = (d.insuredPeople || []).map((ip) => {
        if (!ip.isPrimary) return ip;
        // Clone person data from source
        const updatedPerson = {
          ...ip.person,
          // Copy demographic/personal fields from sourcePerson
          name: sourcePerson.name,
          lastName: sourcePerson.lastName,
          documentType: sourcePerson.documentType,
          documentNumber: sourcePerson.documentNumber,
          birthDate: sourcePerson.birthDate,
          gender: sourcePerson.gender,
          civilState: sourcePerson.civilState,
          heightCm: sourcePerson.heightCm,
          weightLb: sourcePerson.weightLb,
          nacionality: sourcePerson.nacionality,
          isPep: sourcePerson.isPep,
          occupation: sourcePerson.occupation,
          documentExpiresAt: sourcePerson.documentExpiresAt,
        };
        return { ...ip, person: updatedPerson as any };
      });
      return { ...d, insuredPeople: updatedInsured };
    });

    this.quotes.setCurrentQuoteLocal({ ...q, details: updatedDetails });
  }

  /** Load RequestedBy data from current quote into requestedByFg */
  private loadRequestedByFromQuote(): void {
    const q = this.currentQuote;
    if (!q) return;
    this.requestedByFg.patchValue({
      name: q.requestedByName ?? null,
      lastName: q.requestedByLastName ?? null,
      relationship: q.requestedByRelationship ?? null,
      street: q.requestedByStreet ?? q.requestedByAddress ?? null,
      stateCode: q.requestedByStateCode ?? null,
      zipFullDescCode: q.requestedByZipCode ?? null,
      documentNumber: q.requestedByDocumentNumber ?? null,
      personeria: q.requestedByPersoneria ?? Personeria.Fisica,
      tipoComprobante: q.ncfType ?? null,
    });

    // Load requestedBy zips based on saved stateCode
    if (q.requestedByStateCode != null) {
      this.locations.getZipFullDescs(q.requestedByStateCode).subscribe({
        next: (zips) => { this.requestedByZipFullDescs = zips || []; },
      });
    }

    if (q.ncfType != null) {
      this.personFg.patchValue({
        tipoComprobante: q.ncfType,
      });
    }
  }

  private buildQuestionForm(group: InsuranceQuestionGroupDto): void {
    const controls: Record<string, any> = {};
    const detailId = this.selectedPersonCtx?.detailId;
    const detail = detailId
      ? this.currentQuote?.details?.find((d) => d.id === detailId)
      : this.currentQuote?.details?.[0];
    const existingAnswers = detail?.answers || [];
    const byQ = new Map<number, string | null>();
    existingAnswers.forEach((a) => {
      if (a.questionId != null) byQ.set(a.questionId, a.value ?? null);
    });
    (group.questions || []).forEach((q) => {
      if (q?.id == null) return;
      const key = this.qKey(q.id);
      let init: any = null;
      const prev = byQ.get(q.id);
      if (q.type === QuestionType.Bool || q.type === QuestionType.Conditional) {
        init = prev != null ? prev === 'true' : null;
      } else if (q.type === QuestionType.Text) {
        init = prev ?? null;
      } else if (q.type === QuestionType.File) {
        // Preserve file name from fileSelections if a file was previously selected
        const existingFile = this.fileSelections.get(q.id);
        // Fallback to previously saved answer (upload ID) if file was already uploaded
        init = existingFile ? existingFile.name : (prev ?? null);
      }
      const validators = [] as any[];
      if (q.required) validators.push(Validators.required);
      controls[key] = [init, validators];
    });
    this.questionsFg = this.fb.group(controls);
  }

  private saveQuestions(): void {
    this.syncContactDataToQuote();
    const group = this.activeQuestionGroup;
    const q = this.currentQuote;
    if (!group || !q?.quoteId || !q.details?.length) return;

    // Compute visible questions for dependency-based visibility
    const visibleQuestions = this.getVisibleQuestions(group);

    // Validate only visible controls
    visibleQuestions.forEach((ques) => {
      if (ques?.id == null) return;
      const ctrl = this.questionsFg.get(this.qKey(ques.id));
      ctrl?.markAsTouched();
      ctrl?.updateValueAndValidity({ onlySelf: true });
    });
    const hasInvalidVisible = visibleQuestions.some((ques) => {
      const ctrl =
        ques?.id != null ? this.questionsFg.get(this.qKey(ques.id)) : null;
      return !!ctrl && ctrl.invalid;
    });
    if (hasInvalidVisible) return;

    // Check for CancelOnValue violations
    for (const ques of visibleQuestions) {
      if (!ques?.id || !ques.cancelOnValue) continue;
      const key = this.qKey(ques.id);
      const val = this.questionsFg.get(key)?.value;

      let rejected = false;
      if (ques.type === 'File') {
        const fileProvided =
          this.fileSelections.has(ques.id) || (val && typeof val === 'string'); // Assuming string val is prev uploaded URL/ID
        if (ques.cancelOnValue === 'FileProvided' && fileProvided)
          rejected = true;
        if (ques.cancelOnValue === 'FileNotProvided' && !fileProvided)
          rejected = true;
      } else if (ques.type === 'Bool' || ques.type === 'Conditional') {
        // Form 'val' is boolean
        const valStr = String(!!val); // 'true' or 'false'
        // Compare with cancelOnValue (which should be 'true' or 'false' string)
        if (valStr.toLowerCase() === ques.cancelOnValue.toLowerCase())
          rejected = true;
      } else {
        // Text / Default
        if (String(val) === ques.cancelOnValue) rejected = true;
      }

      if (rejected) {
        this.swal.error(
          'Lo sentimos, su respuesta a "' +
          (ques.title || 'una pregunta') +
          '" impide continuar con el proceso.',
        );
        return;
      }
    }

    // Files upload when selected; every visible selected file must have finished uploading
    const pendingFiles = Array.from(this.fileSelections.keys())
      .filter((qId) => visibleQuestions.some((vq) => vq.id === qId))
      .map((qId) => ({ qId, state: this.uploadStates.get(qId) }));
    if (pendingFiles.some((f) => f.state?.status === 'uploading')) {
      this.swal.error('Espera a que termine la carga de los archivos.', 'info');
      return;
    }
    const failed = pendingFiles.filter((f) => f.state?.status !== 'done');
    if (failed.length) {
      // e.g. a previous attempt failed: retry them and let the user continue once done
      failed.forEach((f) => this.startUpload(f.qId));
      this.swal.error('Hay archivos que no se pudieron subir. Los estamos reintentando; presiona "Continuar" cuando terminen.', 'warning');
      return;
    }

    const detailId = this.selectedPersonCtx?.detailId;
    const detail = detailId
      ? q.details.find((d) => d.id === detailId)
      : q.details[0];

    if (!detail) return;

    // Show loading while saving
    this.swal.showLoading('Guardando...');

    const finalizeUpdate = (fileAnswerMap: Map<number, string>) => {
      // Build upserts for visible questions
      const newUpserts: QuoteAnswerUpsertDto[] = [];
      visibleQuestions.forEach((ques) => {
        if (ques?.id == null) return;
        const key = this.qKey(ques.id);
        let value: string | null = null;
        if (ques.type === QuestionType.File) {
          value = fileAnswerMap.get(ques.id) || this.questionsFg.get(key)?.value || null;
        } else if (
          ques.type === QuestionType.Bool ||
          ques.type === QuestionType.Conditional
        ) {
          const v = this.questionsFg.get(key)?.value;
          value = v == null ? null : String(!!v);
        } else {
          value = this.questionsFg.get(key)?.value ?? null;
        }
        newUpserts.push({ questionId: ques.id, value });
      });

      // Merge with existing answers of the target detail
      const existing = detail.answers || [];
      const byQId = new Map<
        number,
        { id?: number | null; questionId?: number; value?: string | null }
      >();
      existing.forEach((a) => {
        if (a?.questionId != null)
          byQId.set(a.questionId, {
            id: a.id ?? null,
            questionId: a.questionId,
            value: a.value ?? null,
          });
      });
      newUpserts.forEach((nu) => {
        const prev =
          nu.questionId != null ? byQId.get(nu.questionId) : undefined;
        byQId.set(nu.questionId!, {
          id: prev?.id ?? null,
          questionId: nu.questionId,
          value: nu.value ?? null,
        });
      });
      const mergedAnswersForDetail = Array.from(byQId.values()).map((x) => ({
        id: x.id ?? (undefined as any),
        questionId: x.questionId,
        value: x.value,
      }));

      // Map current quote details using shared helpers
      const details: QuoteDetailUpsertDto[] = (q.details || []).map((d) => {
        const form = this.getDetailForm(d.id!);
        const fv = form ? form.getRawValue() : {};
        const answers =
          d.id === detail.id
            ? mergedAnswersForDetail
            : (d.answers || []).map((a: any) => this.mapAnswerToUpsert(a));

        return this.mapDetailToUpsert(d, {
          answers,
          formValues: fv,
        });
      });

      this.quotes
        .updateQuote(q.quoteId!, {
          details,
          ...this.getQuoteLevelPayload(),
          ...this.getApplicantPayload()
        })
        .subscribe({
          next: (updatedQuote) => {
            this.swal.dismiss();
            this.closeModal();
            this.activeQuestionGroup = null;
            this.questionsFg.reset();
            this.fileSelections.clear();
            this.resetUploads();
            if (updatedQuote) {
              this.quotes.setCurrentQuoteLocal(updatedQuote);
              this.initPerDetailStateFromQuote(updatedQuote);
              this.loadSummary(); // refresh summary as well
            }
          },
          error: (err) => {
            console.error('Failed to update quote answers', err);
          },
        });
    };

    const uploadedIds = new Map<number, string>();
    pendingFiles.forEach((f) => {
      if (f.state?.uploadId) uploadedIds.set(f.qId, f.state.uploadId);
    });
    finalizeUpdate(uploadedIds);
  }

  /**
   * Document expiration date rules (RN-01..RN-03): a cédula has none (field disabled and empty);
   * a passport requires it, capped at today + passportExpirationYears and prefilled with that date.
   */
  private applyDocumentTypeRules(): void {
    const expiry = this.personFg.get('documentExpiresAt');
    if (!expiry) return;
    const type = this.personFg.get('documentType')?.value;

    if (type === 'Passport') {
      expiry.setValidators([Validators.required, passportExpiryValidator(() => this.passportExpiryMax)]);
      if (expiry.disabled) expiry.enable({ emitEvent: false });
      if (!expiry.value) {
        expiry.setValue(toDisplayDate(this.passportExpiryMax), { emitEvent: false });
      }
    } else {
      expiry.clearValidators();
      if (expiry.value) expiry.setValue(null, { emitEvent: false });
      if (expiry.enabled) expiry.disable({ emitEvent: false });
    }
    expiry.updateValueAndValidity({ emitEvent: false });
  }

  private loadPrincipalInsuredIntoForm(detailId?: number): void {
    const q = this.currentQuote;
    this.personFg.reset();
    this.principalInsuredIds = null;
    if (!q?.details?.length) return;

    let d = q.details[0];
    if (detailId) {
      const found = q.details.find((x) => x.id === detailId);
      if (found) d = found;
      // Also update context if not already set (though openModal sets it)
      if (this.activeModal === 'info-person') {
        // If we came here without openModal setting context (unlikely for principal), ensure it.
      }
    }

    const principal = d.insuredPeople?.find((ip) => ip.isPrimary);
    if (!principal) return;
    this.principalInsuredIds = {
      insuredId: principal.id ?? null,
      personId: principal.personId ?? principal.person?.id ?? null,
    };
    const p = principal.person;
    const hasUserData = !!p?.name;
    const h = cmToFeetInches(p?.heightCm ?? null);
    this.personFg.patchValue({
      name: p?.name ?? null,
      lastName: p?.lastName ?? null,
      documentType: hasUserData ? (p?.documentType ?? null) : null,
      documentNumber: p?.documentNumber ?? null,
      documentExpiresAt: toDisplayDate(p?.documentExpiresAt) ?? null,
      birthDate: toDisplayDate(p?.birthDate) ?? null,
      gender: this.mapGenderFromResponse(p?.gender ?? null),
      civilState: hasUserData ? (p?.civilState ?? null) : null,
      heightFeet: h.feet,
      heightInches: h.inches,
      heightCm: p?.heightCm ?? null,
      weightLb: p?.weightLb ?? null,
      // weightLb is always pounds; display it in pounds by default (unit choice is a
      // checkout-only UI preference, not something persisted from earlier steps).
      weightDisplay: p?.weightLb ?? null,
      weightUnit: 'lb',
      nacionality: hasUserData ? (p?.nacionality ?? null) : null,
      isPep: this.resolveIsPepForForm(p),
      occupation: p?.occupation ?? null,
      primaryInsuredRelation: p?.primaryInsuredRelation ?? null,
    });
    this.applyDocumentTypeRules();

    // Per-detail contact/address are initialized separately in initPerDetailStateFromQuote.
    // No assignments needed here to avoid conflicting global state.
  }

  private mapGenderFromResponse(
    value: string | null | undefined,
  ): Gender | null {
    if (!value) return null;
    const v = value.toUpperCase();
    if (v === 'M' || v === 'MASCULINO') return Gender.M;
    if (v === 'F' || v === 'FEMENINO') return Gender.F;
    return null;
  }

  private mapGenderToRequest(
    value: string | Gender | null | undefined,
  ): Gender | null {
    if (!value) return null;
    if (value === Gender.M || value === Gender.F) return value;
    const v = String(value).toLowerCase();
    if (v === 'masculino' || v === 'm') return Gender.M;
    if (v === 'femenino' || v === 'f') return Gender.F;
    return null;
  }

  private resolveIsPepForForm(person: any): boolean | null {
    if (!person) return null;
    if (person.isPep === true) return true;
    if (person.isPep === false) {
      // If the user explicitly answered (in-memory flag) OR the person was
      // already saved with real data (name present), treat false as an
      // explicit "No".  The isPepAnswered flag is volatile (not persisted),
      // so after a page reload we fall back to checking whether the person
      // record has substantive data.
      if (person.isPepAnswered === true || !!person.name) return false;
      return null;
    }
    return null;
  }

  private loadInsuredIntoForm(detailId: number, insuredId: number): void {
    const q = this.currentQuote;
    this.personFg.reset();
    this.selectedPersonCtx = null;
    if (!q?.details?.length) return;
    const d = q.details.find((x) => x.id === detailId);
    if (!d?.insuredPeople?.length) return;
    const target = d.insuredPeople.find((ip) => ip.id === insuredId);
    if (!target) return;
    const p = target.person;
    const h = cmToFeetInches(p?.heightCm ?? null);
    this.selectedPersonCtx = {
      detailId: d.id!,
      insuredId: target.id!,
      personId: target.personId ?? p?.id ?? null,
    };
    const hasUserData = !!p?.name;
    this.personFg.patchValue({
      name: p?.name ?? null,
      lastName: p?.lastName ?? null,
      documentType: hasUserData ? (p?.documentType ?? null) : null,
      documentNumber: p?.documentNumber ?? null,
      documentExpiresAt: toDisplayDate(p?.documentExpiresAt) ?? null,
      birthDate: toDisplayDate(p?.birthDate) ?? null,
      gender: this.mapGenderFromResponse(p?.gender ?? null),
      civilState: hasUserData ? (p?.civilState ?? null) : null,
      nacionality: hasUserData ? (p?.nacionality ?? null) : null,
      heightFeet: h.feet,
      heightInches: h.inches,
      heightCm: p?.heightCm ?? null,
      weightLb: p?.weightLb ?? null,
      weightDisplay: p?.weightLb ?? null,
      weightUnit: 'lb',
      isPep: this.resolveIsPepForForm(p),
      occupation: p?.occupation ?? null,
      primaryInsuredRelation: p?.primaryInsuredRelation ?? null,
    });
    this.applyDocumentTypeRules();
  }

  private saveOtherInsured(): void {
    if (this.personFg.invalid) {
      this.personFg.markAllAsTouched();
      scrollToFirstInvalidControl(this.el);
      this.swal.error(
        'Por favor, complete todos los campos requeridos del asegurado.',
      );
      return;
    }

    // IMPORTANT: Capture personFg values BEFORE syncContactDataToQuote because
    // syncContactDataToQuote → setCurrentQuoteLocal → currentQuote$ subscription
    // → loadPrincipalInsuredIntoForm → personFg.reset(), which wipes the form.
    const v = this.personFg.value as any;
    const pepAnswered = v.isPep === true || v.isPep === false;

    this.syncContactDataToQuote();
    const ctx = this.selectedPersonCtx;
    const q = this.currentQuote;
    if (!ctx || !q?.quoteId || !q.details?.length) return;

    // Validate active policy before saving
    this.swal.showLoading('Validando...');
    this.quotes.validateActivePolicy(q.quoteId, v.documentNumber ?? '').subscribe({
      next: (result) => {
        if (result?.hasActivePolicy) {
          this.swal.error(result.message || 'El asegurado ya posee una póliza activa de este producto.');
          return;
        }
        this.executeSaveOtherInsured(q, ctx, v, pepAnswered);
      },
      error: (err) => {
        console.error('Error validating active policy', err);
        this.executeSaveOtherInsured(q, ctx, v, pepAnswered);
      },
    });
  }

  private executeSaveOtherInsured(
    q: QuoteResponseDto,
    ctx: { detailId: number; insuredId: number; personId: number | null },
    v: any,
    pepAnswered: boolean,
  ): void {
    if (!q?.quoteId || !q.details?.length) return;

    const details: QuoteDetailUpsertDto[] = q.details.map((d) => {
      const form = this.getDetailForm(d.id!);
      const fv = form ? form.getRawValue() : {};

      // For non-target details, use shared mapper as-is
      if (d.id !== ctx.detailId) {
        return this.mapDetailToUpsert(d, { formValues: fv });
      }

      // For the target detail, override only the target insured person
      const insuredPeople: QuoteInsuredPersonUpsertDto[] = (
        d.insuredPeople || []
      ).map((ip) => {
        if (ip.id !== ctx.insuredId) {
          return this.mapInsuredToUpsert(ip, fv);
        }
        // Target insured — build person from form values
        return {
          id: ip.id ?? null,
          personId: ip.personId ?? ip.person?.id ?? null,
          isPrimary: ip.isPrimary,
          person: {
            id: ctx.personId ?? ip.person?.id ?? null,
            name: v.name ?? null,
            lastName: v.lastName ?? null,
            documentType: v.documentType ?? null,
            documentNumber: v.documentNumber ?? null,
            documentExpiresAt: toIsoDate(v.documentExpiresAt) ?? null,
            birthDate: toIsoDate(v.birthDate) ?? null,
            gender: this.mapGenderToRequest(v.gender),
            civilState: v.civilState ?? null,
            heightCm: v.heightCm ?? null,
            weightLb: v.weightLb ?? null,
            nacionality: v.nacionality ?? null,
            personAddressId: v.personAddressId ?? null,
            isPep: v.isPep ?? null,
            occupation: v.occupation ?? null,
            primaryInsuredRelation: v.primaryInsuredRelation ?? null,
          },
          contactEmail: ip.contactEmail,
          homePhone: ip.homePhone,
          mobilePhone: ip.mobilePhone,
        } as QuoteInsuredPersonUpsertDto;
      });

      return this.mapDetailToUpsert(d, { insuredPeople });
    });

    this.swal.showLoading('Guardando...');
    this.quotes.updateQuote(q.quoteId!, {
      details,
      ...this.getQuoteLevelPayload(),
      ...this.getApplicantPayload()
    }).subscribe({
      next: (updatedQuote) => {
        this.swal.dismiss();
        this.closeModal();
        if (updatedQuote) {
          const updatedDetail = updatedQuote.details?.find((d) => d.id === ctx.detailId);
          const updatedInsured = updatedDetail?.insuredPeople?.find((ip) => ip.id === ctx.insuredId);
          if (updatedInsured?.person) {
            (updatedInsured.person as any).isPep = v.isPep ?? null;
            (updatedInsured.person as any).isPepAnswered = pepAnswered;
          }
          this.quotes.setCurrentQuoteLocal(updatedQuote);
          this.initPerDetailStateFromQuote(updatedQuote);
          this.loadSummary();
        }
      },
      error: (err) => {
        console.error('Failed to update other insured', err);
      },
    });
  }

  private savePrincipalInsured(): void {
    if (this.personFg.invalid) {
      this.personFg.markAllAsTouched();
      scrollToFirstInvalidControl(this.el);
      this.swal.error(
        'Por favor, complete todos los campos requeridos del asegurado.',
      );
      return;
    }

    const q = this.currentQuote;
    if (!q?.quoteId || !q.details) return;

    // Validate active policy before saving
    const pv = this.personFg.value as any;
    this.swal.showLoading('Validando...');
    this.quotes.validateActivePolicy(q.quoteId, pv.documentNumber ?? '').subscribe({
      next: (result) => {
        if (result?.hasActivePolicy) {
          this.swal.error(result.message || 'El asegurado ya posee una póliza activa de este producto.');
          return;
        }
        this.executeSavePrincipalInsured(q);
      },
      error: (err) => {
        console.error('Error validating active policy', err);
        // On validation error, proceed with save to avoid blocking the user
        this.executeSavePrincipalInsured(q);
      },
    });
  }

  private executeSavePrincipalInsured(q: QuoteResponseDto): void {
    if (!q?.quoteId || !q.details) return;

    // Determine target detail
    let targetDetailId: number | null = null;
    // Try to get from context first
    if (this.selectedPersonCtx?.detailId) {
      targetDetailId = this.selectedPersonCtx.detailId;
    } else {
      // Fallback to first detail (legacy behavior)
      targetDetailId = q.details[0]?.id ?? null;
    }

    if (!targetDetailId) return;

    // Check if we are editing the "Master" (the one everyone is syncing from)
    const isMaster = this.useAsPrimaryForAllDetailId === targetDetailId;

    // If we are editing a slave, we should break the sync because data now differs
    // Unless the user intends to make THIS the new master?
    // For now, if editing a slave, just set sync to null.
    if (this.useAsPrimaryForAllDetailId && !isMaster) {
      this.useAsPrimaryForAllDetailId = null;
    }

    const fg = this.personFg;
    const v = fg.value as any;
    const pepAnswered = v.isPep === true || v.isPep === false;

    const details: QuoteDetailUpsertDto[] = q.details.map((d) => {
      const form = this.getDetailForm(d.id!);
      const fv = form ? form.getRawValue() : {};

      // We update the detail if:
      // 1. It is the target detail we are editing.
      // 2. OR we are editing the Master and sync is enabled (so we update everyone).
      const shouldUpdate =
        d.id === targetDetailId ||
        (isMaster && this.useAsPrimaryForAllDetailId);

      if (!shouldUpdate) {
        // Use shared mapper for non-target details
        return this.mapDetailToUpsert(d, { formValues: fv });
      }

      const insuredPeople: QuoteInsuredPersonUpsertDto[] = (
        d.insuredPeople || []
      ).map((ip) => {
        const isPrimary = !!ip.isPrimary;

        if (!isPrimary) {
          // Non-primary: use shared mapper
          return this.mapInsuredToUpsert(ip, fv);
        }

        // Primary insured — build person from form values
        // Determine if we need to fork the person record
        let shouldFork = false;
        if (
          d.id === targetDetailId &&
          !isMaster &&
          this.principalInsuredIds?.personId
        ) {
          const currentPersonId = this.principalInsuredIds.personId;
          const isShared = (q.details || []).some(
            (other) =>
              other.id !== targetDetailId &&
              other.insuredPeople?.some(
                (oip) =>
                  oip.isPrimary &&
                  (oip.personId === currentPersonId ||
                    oip.person?.id === currentPersonId),
              ),
          );
          if (isShared) {
            shouldFork = true;
          }
        }

        const addressFromForm = {
          street: fv.street ?? null,
          countryCode: fv.countryCode ?? null,
          stateCode: fv.stateCode ?? null,
          zipCode: fv.zipFullDescCode ?? null,
        };

        const person = {
          id: shouldFork
            ? null
            : ((d.id === targetDetailId
              ? this.principalInsuredIds?.personId
              : ip.personId) ??
              ip.person?.id ??
              null),
          name: v.name ?? null,
          lastName: v.lastName ?? null,
          documentType: v.documentType ?? null,
          documentNumber: v.documentNumber ?? null,
          documentExpiresAt: toIsoDate(v.documentExpiresAt) ?? null,
          birthDate: toIsoDate(v.birthDate) ?? null,
          gender: this.mapGenderToRequest(v.gender),
          civilState: v.civilState ?? null,
          heightCm: v.heightCm ?? null,
          weightLb: v.weightLb ?? null,
          nacionality: v.nacionality ?? null,
          personAddressId: v.personAddressId ?? null,
          isPep: v.isPep ?? null,
          occupation: v.occupation ?? null,
          primaryInsuredRelation: v.primaryInsuredRelation ?? null,
          ...(this.hasValidAddress(addressFromForm)
            ? { address: addressFromForm }
            : this.hasValidAddress(ip.person?.address)
              ? { address: ip.person!.address }
              : {}),
        };

        const finalEmail = fv.contactEmail || ip.contactEmail;
        const finalHome = fv.homePhone || ip.homePhone;
        const finalMobile = fv.mobilePhone || ip.mobilePhone;

        return {
          id: ip.id ?? null,
          personId: shouldFork ? null : (ip.personId ?? ip.person?.id ?? null),
          isPrimary: ip.isPrimary,
          person,
          ...(finalEmail ? { contactEmail: finalEmail } : {}),
          ...(finalHome ? { homePhone: finalHome } : {}),
          ...(finalMobile ? { mobilePhone: finalMobile } : {}),
        } as QuoteInsuredPersonUpsertDto;
      });

      return this.mapDetailToUpsert(d, { insuredPeople });
    });

    this.swal.showLoading('Guardando...');
    this.quotes
      .updateQuote(q.quoteId!, {
        details,
        ...this.getQuoteLevelPayload(),
        ...this.getApplicantPayload()
      })
      .subscribe({
        next: (updatedQuote) => {
          this.swal.dismiss();
          this.closeModal();
          // Update local quote so UI reflects changes immediately (especially for sync)
          if (updatedQuote) {
            (updatedQuote.details || []).forEach((d) => {
              const shouldUpdate =
                d.id === targetDetailId ||
                (isMaster && this.useAsPrimaryForAllDetailId);
              if (!shouldUpdate) return;
              const primary = (d.insuredPeople || []).find((ip) => !!ip.isPrimary);
              if (!primary?.person) return;
              (primary.person as any).isPep = v.isPep ?? null;
              (primary.person as any).isPepAnswered = pepAnswered;
            });
            this.currentQuote = updatedQuote;
            this.initPerDetailStateFromQuote(updatedQuote);
          }
        },
        error: (err) => {
          console.error(
            'Failed to update quote with principal insured data',
            err,
          );
        },
      });
  }

  // Refactored from original onPayNow to be callable after saving requestedBy
  private updateDetailsAndPay(q: QuoteResponseDto, extraPayload: any = {}): void {
    let masterPrimaryPerson: any = null;
    if (this.useAsPrimaryForAllDetailId) {
      const masterDetail = q.details?.find((d) => d.id === this.useAsPrimaryForAllDetailId);
      masterPrimaryPerson = masterDetail?.insuredPeople?.find((ip) => ip.isPrimary)?.person;
    }

    // Build details payload using shared helpers; only primary insured overrides
    // are done inline to apply sync logic and form-based address/contact.
    const details: QuoteDetailUpsertDto[] = (q.details || []).map((d) => {
      const form = this.getDetailForm(d.id!);
      const fv = form ? form.getRawValue() : {};

      const insuredPeople = (d.insuredPeople || []).map((ip) => {
        if (!ip.isPrimary) {
          // Non-primary: use shared mapper — fixes previous data loss
          return this.mapInsuredToUpsert(ip);
        }

        // Primary: apply sync logic if needed
        const isSyncTarget = this.useAsPrimaryForAllDetailId && d.id !== this.useAsPrimaryForAllDetailId;
        const basePersonData: any = (isSyncTarget && masterPrimaryPerson) ? masterPrimaryPerson : (ip.person || {});

        const addressFromForm = {
          street: fv.street ?? null,
          countryCode: fv.countryCode ?? null,
          stateCode: fv.stateCode ?? null,
          zipCode: fv.zipFullDescCode ?? null,
        };

        const person = {
          ...basePersonData,
          id: ip.personId ?? ip.person?.id ?? null,
          gender: this.mapGenderToRequest(basePersonData.gender),
          isPep: basePersonData.isPep ?? null,
          occupation: basePersonData.occupation ?? null,
          nacionality: basePersonData.nacionality ?? null,
          ...(this.hasValidAddress(addressFromForm)
            ? { address: addressFromForm }
            : {}),
        } as any;
        return {
          id: ip.id ?? null,
          personId: ip.personId ?? ip.person?.id ?? null,
          isPrimary: ip.isPrimary,
          person,
          contactEmail: fv.contactEmail ?? null,
          homePhone: fv.homePhone ?? null,
          mobilePhone: fv.mobilePhone ?? null,
        } as QuoteInsuredPersonUpsertDto;
      });

      return this.mapDetailToUpsert(d, { insuredPeople });
    });
    this.swal.showLoading('Guardando...');
    this.quotes.updateQuote(q.quoteId!, { details, ...this.getQuoteLevelPayload(), ...extraPayload }).subscribe({
      next: () => {
        this.swal.dismiss();
        this.openModal('payment', 'Detalles del pago', 'credit-card');
      },
      error: (err) => {
        console.error('Error guardando la cotización antes del pago', err);
      },
    });
  }

  // ── Shared mapper helpers (single source of truth for update payloads) ──

  /** Map a QuoteCoverageDto to the request DTO used in update payloads. */
  private mapCoverageToRequest(c: any, currency: string | null): QuoteCoverageRequestDto {
    return {
      id: c.id ?? null,
      coverageId: c.coverage?.id ?? c.coverageId ?? null,
      coverageCode: c.coverage?.coverageCode ?? c.coverageCode ?? null,
      name: c.name ?? '',
      description: c.description ?? '',
      amount: c.amount ?? 0,
      currency: currency ?? '',
    };
  }

  /** Map a QuoteAnswerDto to the upsert DTO used in update payloads. */
  private mapAnswerToUpsert(a: any): QuoteAnswerUpsertDto {
    return {
      id: a.id ?? null,
      questionId: a.questionId,
      value: a.value ?? '',
    };
  }

  /**
   * Map a QuoteInsuredPersonDto to the upsert DTO used in update payloads.
   * Always includes the full person object and contact fields so that no
   * data is lost on update regardless of which save path calls this.
   *
   * @param ip       The insured person DTO from the current quote response.
   * @param formOverrides Optional per-detail form values to pick up
   *                      address / contact changes from the detail form.
   */
  private mapInsuredToUpsert(ip: any, formOverrides?: any): QuoteInsuredPersonUpsertDto {
    const fv = formOverrides ?? {};
    const addressFromForm = fv.street != null ? {
      street: fv.street ?? null,
      countryCode: fv.countryCode ?? null,
      stateCode: fv.stateCode ?? null,
      zipCode: fv.zipCode ?? fv.zipFullDescCode ?? null,
    } : null;

    const person = ip.person
      ? {
        id: ip.personId ?? ip.person?.id ?? null,
        name: ip.person.name,
        lastName: ip.person.lastName,
        documentType: ip.person.documentType,
        documentNumber: ip.person.documentNumber,
        documentExpiresAt: ip.person.documentExpiresAt,
        birthDate: ip.person.birthDate,
        gender: this.mapGenderToRequest(ip.person.gender),
        civilState: ip.person.civilState,
        heightCm: ip.person.heightCm,
        weightLb: ip.person.weightLb,
        nacionality: ip.person.nacionality,
        personAddressId: ip.person.personAddressId,
        isPep: ip.person.isPep,
        occupation: ip.person.occupation,
        primaryInsuredRelation: ip.person.primaryInsuredRelation,
        ...(this.hasValidAddress(addressFromForm)
          ? { address: addressFromForm }
          : this.hasValidAddress(ip.person?.address)
            ? { address: ip.person!.address }
            : {}),
      }
      : undefined;

    return {
      id: ip.id ?? null,
      personId: ip.personId ?? ip.person?.id ?? null,
      isPrimary: ip.isPrimary,
      person,
      contactEmail: fv.contactEmail || ip.contactEmail,
      homePhone: fv.homePhone || ip.homePhone,
      mobilePhone: fv.mobilePhone || ip.mobilePhone,
    } as QuoteInsuredPersonUpsertDto;
  }

  /**
   * Map a full QuoteDetailDto to a QuoteDetailUpsertDto using the shared
   * helpers above.  All insured people, coverages and answers are mapped
   * consistently.
   *
   * @param d              The detail from the current quote response.
   * @param overrides      Optional object to override specific sub-arrays:
   *                       - insuredPeople: pre-built array replaces the default mapping
   *                       - answers: pre-built array replaces the default mapping
   *                       - formValues: raw form values forwarded to mapInsuredToUpsert
   */
  private mapDetailToUpsert(
    d: any,
    overrides?: {
      insuredPeople?: QuoteInsuredPersonUpsertDto[];
      answers?: QuoteAnswerUpsertDto[];
      formValues?: any;
    },
  ): QuoteDetailUpsertDto {
    const fv = overrides?.formValues;

    const insuredPeople =
      overrides?.insuredPeople ??
      (d.insuredPeople || []).map((ip: any) => this.mapInsuredToUpsert(ip, fv));

    const coverages = (d.coverages || []).map((c: any) =>
      this.mapCoverageToRequest(c, d.currency),
    );

    const answers =
      overrides?.answers ??
      (d.answers || []).map((a: any) => this.mapAnswerToUpsert(a));

    const payload: QuoteDetailUpsertDto = {
      id: d.id ?? null,
      typeId: d.typeId,
      selectedPlanId: d.selectedPlanId ?? null,
      durationYears: d.durationYears ?? null,
      currency: d.currency ?? null,
      insuredPeople,
      coverages,
      answers,
    } as QuoteDetailUpsertDto;

    // Beneficiaries: sent only when the product requires them and the list is complete (sums 100%).
    // Otherwise omitted so intermediate saves (insured data, questions) don't fail and the backend keeps
    // what it has; "Pagar" validates the list before the final save.
    const beneficiariesArray = d.id != null ? this.getBeneficiariesArray(d.id) : null;
    if (beneficiariesArray && d.insuranceType?.allowBeneficiaries && beneficiariesArray.valid) {
      payload.beneficiaries = mapBeneficiariesToUpsert(beneficiariesArray);
    }

    return payload;
  }

  getBeneficiariesArray(detailId: number): FormArray | null {
    const ctrl = this.getDetailForm(detailId)?.get('beneficiaries');
    return ctrl instanceof FormArray ? ctrl : null;
  }

  /**
   * Adds the per-policy beneficiaries FormArray to the detail form (once), seeded from the quote.
   * Products flagged with allowBeneficiaries require at least one row whose percentages add up to 100.
   */
  private ensureBeneficiariesArray(d: QuoteDetailDto): void {
    const form = this.getDetailForm(d.id!);
    if (!form) return;

    const requires = !!d.insuranceType?.allowBeneficiaries;
    let array = this.getBeneficiariesArray(d.id!);
    if (!array) {
      array = this.fb.array(
        (d.beneficiaries ?? []).map((b) => buildBeneficiaryGroup(this.fb, b)),
      );
      form.addControl('beneficiaries', array, { emitEvent: false });
    } else if (array.length === 0 && (d.beneficiaries?.length ?? 0) > 0) {
      (d.beneficiaries ?? []).forEach((b) => array!.push(buildBeneficiaryGroup(this.fb, b), { emitEvent: false }));
    }

    array.setValidators(
      requires
        ? [minBeneficiariesValidator(1), beneficiaryPercentageSumValidator()]
        : [beneficiaryPercentageSumValidator()],
    );
    if (requires && array.length === 0) {
      array.push(buildBeneficiaryGroup(this.fb), { emitEvent: false });
    }
    array.updateValueAndValidity({ emitEvent: false });
  }

  /** Returns the first beneficiaries validation message, marking the lists as touched; null when all are valid. */
  private validateBeneficiaries(): string | null {
    const q = this.currentQuote;
    let message: string | null = null;
    (q?.details ?? []).forEach((d) => {
      const array = d.id != null ? this.getBeneficiariesArray(d.id) : null;
      if (!array || !d.insuranceType?.allowBeneficiaries || array.valid) return;
      array.markAllAsTouched();
      if (message) return;
      if (array.hasError('minBeneficiaries')) {
        message = `Debe agregar al menos un beneficiario para ${d.insuranceType?.name ?? 'la póliza'}.`;
      } else if (array.controls.some((c) => c.invalid)) {
        message = 'Complete los datos de los beneficiarios (nombre, documento, parentesco y porcentaje).';
      } else if (array.hasError('percentageSum')) {
        message = 'Los porcentajes de los beneficiarios deben sumar 100%.';
      }
    });
    return message;
  }

  /** Returns top-level quote scalar fields to include in every update call. */
  private getQuoteLevelPayload(): Partial<UpdateQuoteRequestDto> {
    const q = this.currentQuote;
    return {
      quoteEmail: q?.quoteEmail ?? null,
      contactEmail: q?.contactEmail ?? null,
      producerCode: denormalizeProducerCode(q?.producerCode),
      retailer: q?.retailer ?? null,
      supervisor: q?.supervisor ?? null,
      manager: q?.manager ?? null,
      branch: q?.branch ?? null,
      physicalDelivery: q?.physicalDelivery ?? null,
    };
  }

  private hasValidAddress(address: any): boolean {
    if (!address) return false;
    return !!(
      address.street &&
      address.countryCode &&
      address.stateCode
    );
  }

  /** Look up the selected ZipFullDescDto by zipCode from the cached per-detail list */
  private findSelectedZipFullDesc(detailId: number, zipCode: number | null): ZipFullDescDto | null {
    if (zipCode == null) return null;
    const list = this.zipFullDescsByDetail.get(detailId) ?? [];
    return list.find(z => z.zipCode === zipCode) ?? null;
  }

  /**
   * Validates primary insured data for ALL details.
   * Returns the detailId of the first failing detail, or null if all are valid.
   */
  private validatePrimaryInsuredData(q: QuoteResponseDto): number | null {
    const requiredFields = [
      'name',
      'lastName',
      'documentType',
      'documentNumber',
      'birthDate',
      'gender',
      'civilState',
      'heightCm',
      'weightLb',
      'nacionality',
    ];

    for (const detail of q.details ?? []) {
      // If sync is enabled, skip validation for non-master details 
      // since they will inherit the master detail's data at save time
      if (this.useAsPrimaryForAllDetailId && detail.id !== this.useAsPrimaryForAllDetailId) {
        continue;
      }

      const primary = detail.insuredPeople?.find((i) => i.isPrimary);
      if (!primary) return detail.id!;

      const p = primary.person;
      if (!p) return detail.id!;

      for (const field of requiredFields) {
        const val = (p as any)[field];
        if (val === null || val === undefined || val === '') {
          console.warn(
            `Primary Insured Validation Failed on detail ${detail.id}: Field '${field}' is missing. Value:`,
            val,
            p,
          );
          return detail.id!;
        }
      }
      if (Number(p.heightCm) <= 0 || Number(p.weightLb) <= 0) {
        return detail.id!;
      }
      // RN-05: a passport needs its expiration date
      if (p.documentType === 'Passport' && !p.documentExpiresAt) {
        return detail.id!;
      }
    }

    return null; // All valid
  }

  private getApplicantPayload(): any {
    const q = this.currentQuote;
    if (this.isContratanteNotInsured) {
      const formVal = this.requestedByFg.getRawValue();
      return {
        requestedByName: formVal.name,
        requestedByLastName: formVal.lastName,
        requestedByRelationship: formVal.relationship,
        requestedByAddress: formVal.street,
        requestedByStreet: formVal.street,
        requestedByCountryCode: formVal.countryCode ?? this.defaultCountryCode,
        requestedByStateCode: formVal.stateCode,
        requestedByZipCode: formVal.zipFullDescCode,
        requestedByDocumentNumber: formVal.documentNumber,
        requestedByPersoneria: formVal.personeria ?? Personeria.Fisica,
        ncfType: formVal.tipoComprobante,
      };
    } else {
      return {
        requestedByName: null,
        requestedByLastName: null,
        requestedByRelationship: null,
        requestedByAddress: null,
        requestedByStreet: null,
        requestedByCountryCode: null,
        requestedByStateCode: null,
        requestedByZipCode: null,
        requestedByDocumentNumber: null,
        requestedByPersoneria: null,
        ncfType: this.requestedByFg.get('tipoComprobante')?.value ?? null,
      };
    }
  }
}
